import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { AiOutlineArrowRight } from "react-icons/ai";

import {
  ExternalDmvStatus,
  Info,
  StatusType,
  TitleDocumentQuery,
  TitleIssue,
  TitleQuery,
  getDmvStatus,
} from "@Types/title_issues";
import Table from "@Components/Table";
import IconButton from "@Components/IconButton";
import { Column } from "@Components/Table/types";
import useGetTitleIssues, {
  useGetTitleIssueStatusCounts,
} from "@Hooks/useTitleIssues";
import TextFilter from "@Components/Filters/TextFilter";
import { formatCustomersNames } from "@Pages/Applications/Applications";

import styles from "./TitleIssues.module.scss";
import { TitleStatusChip } from "./TitleIssueStatusChip/TitleIssueStatusChip";
import TitleIssueStatusCards from "./TitleIssuesStatusCards/TitleIssueStatusCards";
import ApplicantFilter from "@Components/Filters/ApplicantFilter";
import TitleIssuesDocumentStatusCards from "./TitleIssuesDocumentStatusCards/TitleIssuesDocumentStatusCards";
import Grid from "@Components/Grid";
import StatusFilter from "./StatusFilter";
import {
  buildDmvStatusQuery,
  buildDocumentStatusQuery,
} from "./titleIssueStatusQueries";

export const TitleDocumentStatusChip = ({ info }: { info: Info }) => {
  const status = getTitleIssueStatus(info);
  const getStatusClassName = (
    status: "resolved" | "awaiting" | "incomplete"
  ) => {
    if (status === "resolved") return styles.statusResolved;
    if (status === "awaiting" || info.dmvInfo.status === "titled")
      return styles.statusAwaiting;
    return styles.statusIncomplete;
  };

  return (
    <div className={styles.statusSection}>
      <span
        className={`${styles.status} ${getStatusClassName(status)}`}
        role="status"
      >
        {status === "resolved"
          ? "Uploaded"
          : info.dmvInfo.status === "titled"
          ? "Not required"
          : status}
      </span>
    </div>
  );
};

export const getTitleIssueStatus = (data: Info) => {
  const { oldTitleRegistrationInfo, titleRegistrationInfo, receiptInfo } = data;

  const isApproved =
    oldTitleRegistrationInfo.status === "resolved" &&
    titleRegistrationInfo.status === "resolved" &&
    receiptInfo.status === "resolved";

  const isNew =
    oldTitleRegistrationInfo.status === "awaiting" &&
    titleRegistrationInfo.status === "awaiting" &&
    receiptInfo.status === "awaiting";

  if (isApproved) {
    return "resolved";
  } else if (isNew) {
    return "awaiting";
  }
  return "incomplete";
};

export default function TitleIssues() {
  const navigate = useNavigate();

  const [titleQuery, setTitleQuery] = useState<TitleQuery>(undefined);
  const [titleDocumentQuery, setTitleDocumentQuery] =
    useState<TitleDocumentQuery>(undefined);

  const {
    data: statusCounts,
    isLoading: statusCountsLoading,
    error: statusCountsError,
  } = useGetTitleIssueStatusCounts();

  const handleSetFilter = (
    type: "document" | "dmv",
    query: TitleQuery | TitleDocumentQuery
  ) => {
    if (type === "dmv") {
      setTitleQuery(query as TitleQuery);
      setTitleDocumentQuery(undefined);
    } else {
      setTitleDocumentQuery(query as TitleDocumentQuery);
      setTitleQuery(undefined);
    }
  };

  // Both the cards above the table and the column-header filters below drive
  // this same titleQuery/titleDocumentQuery state, so only one DMV status and
  // one document status can ever be active at once, from either place.
  const handleDmvStatusSelect = (status?: string) => {
    if (!status || status === titleQuery?.active) {
      handleSetFilter("dmv", undefined);
    } else {
      handleSetFilter("dmv", {
        active: status as ExternalDmvStatus,
        query: buildDmvStatusQuery(status as ExternalDmvStatus),
      } as TitleQuery);
    }
  };

  const handleDocumentStatusSelect = (status?: string) => {
    if (!status || status === titleDocumentQuery?.active) {
      handleSetFilter("document", undefined);
    } else {
      handleSetFilter("document", {
        active: status as StatusType,
        query: buildDocumentStatusQuery(status as StatusType),
      } as TitleDocumentQuery);
    }
  };

  const columns: Column<TitleIssue>[] = [
    {
      id: "dmv",
      label: "DMV",
      value: (titleIssue) =>
        titleIssue.data.deal?.data?.applicant?.data?.info?.currentState ?? "",
      sortPath: [
        "data",
        "deal",
        "data",
        "applicant",
        "data",
        "info",
        "currentState",
      ],
      filters: [
        {
          placeholder: "DMV",
          preview: TextFilter,
          queryPath: [
            "data",
            "deal",
            "data",
            "applicant",
            "data",
            "info",
            "currentState",
          ],
          partialSearch: true,
          caseInsensitive: true,
        },
      ],
    },
    {
      label: "DMV Status",
      id: "status",
      truncate: 15,
      filters: [
        {
          placeholder: "DMV Status",
          preview: StatusFilter,
          queryPath: ["data", "info", "dmvInfo", "status"],
          valuesForSelect: ["Titled", "Untitled", "Not checked"].map(
            (value) => ({ label: value, value })
          ),
          extraProps: {
            activeValue: titleQuery?.active,
            onSelect: handleDmvStatusSelect,
          },
        },
      ],
      value: (titleIssue) =>
        titleIssue?.data?.info ? (
          <TitleStatusChip
            status={getDmvStatus(titleIssue.data.info.dmvInfo.status)}
          />
        ) : (
          ""
        ),
    },
    {
      label: "Document Status",
      id: "documentStatus",
      truncate: 15,
      filters: [
        {
          placeholder: "Document Status",
          preview: StatusFilter,
          queryPath: ["data", "info"],
          valuesForSelect: [
            { label: "Awaiting", value: "awaiting" },
            { label: "Incomplete", value: "incomplete" },
            { label: "Uploaded", value: "resolved" },
          ],
          extraProps: {
            activeValue: titleDocumentQuery?.active,
            onSelect: handleDocumentStatusSelect,
          },
        },
      ],
      value: (titleIssue) =>
        titleIssue?.data?.info ? (
          <TitleDocumentStatusChip info={titleIssue.data.info} />
        ) : (
          ""
        ),
    },

    {
      label: "Customer",
      id: "customer",
      truncate: 15,
      filters: [
        {
          placeholder: "Customer",
          preview: ApplicantFilter,
          queryPath: [
            ["data", "deal", "data", "applicant", "data", "info", "firstName"],
            ["data", "deal", "data", "applicant", "data", "info", "lastName"],
            [
              "data",
              "deal",
              "data",
              "coApplicant",
              "data",
              "info",
              "firstName",
            ],
            ["data", "deal", "data", "coApplicant", "data", "info", "lastname"],
          ],
        },
      ],
      value: (titleIssue) =>
        titleIssue?.data?.deal
          ? formatCustomersNames(titleIssue?.data?.deal)
          : "",
    },

    {
      id: "actions",
      label: "Actions",
      value: (titleIssue) => (
        <IconButton
          id={`open-titleIssue-${titleIssue?._id}`}
          className={"blueCircle"}
          onClick={() => {
            navigate(`/title_info/${titleIssue._id}`);
          }}
        >
          <AiOutlineArrowRight size={18} />
        </IconButton>
      ),
    },
  ];

  return (
    <div className={styles.tableContainer}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "row",
          width: "100%",
          alignItems: "center",
        }}
      >
        <h2 className={styles.title}>Title Info</h2>
      </div>
      <Grid xs={12} container spacing={1}>
        <Grid xs={6}>
          <TitleIssueStatusCards
            titleQuery={titleQuery}
            handleSetFilter={handleSetFilter}
            counts={statusCounts?.dmvStatus}
            isLoading={statusCountsLoading}
            error={statusCountsError}
          />
        </Grid>

        <Grid xs={6}>
          <TitleIssuesDocumentStatusCards
            titleQuery={titleDocumentQuery}
            handleSetFilter={handleSetFilter}
            counts={statusCounts?.documentStatus}
            isLoading={statusCountsLoading}
            error={statusCountsError}
          />
        </Grid>
      </Grid>

      <Table<TitleIssue>
        columns={columns}
        entityType="title_info"
        query={titleQuery?.query ?? titleDocumentQuery?.query}
        useGetFunction={useGetTitleIssues}
      />
    </div>
  );
}
